
import React, { useState } from 'react';
import './SignInPage.css';

interface SubscriptionTier {
  id: string;
  name: string;
  price: number;
  features: string[];
  isPopular?: boolean;
}

interface SignInPageProps {
  onSignIn: (email: string, password: string, isCreator: boolean) => Promise<void> | void;
  onSignUp?: (email: string, password: string, name: string, selectedTier?: string) => Promise<void>;
  onBack: () => void;
  subscriptionTiers?: SubscriptionTier[];
  /** Initial mode. Defaults to 'signin'. LandingPage's "Get Started"
   *  routes here with 'signup' so the new visitor lands on the Create
   *  Account / Join as Subscriber screen, not the login form. */
  defaultMode?: 'signin' | 'signup';
}

const SignInPage: React.FC<SignInPageProps> = ({ onSignIn, onSignUp, onBack, subscriptionTiers = [], defaultMode = 'signin' }) => {
  const [isSignUp, setIsSignUp] = useState(defaultMode === 'signup');
  // Fan-only signup - no creator option
  const isCreator = false;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [selectedTier, setSelectedTier] = useState<string>('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{[key: string]: string}>({});
  const [loading, setLoading] = useState(false);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});

    const errors: {[key: string]: string} = {};

    if (isSignUp && !name.trim()) {
      errors.name = 'Name is required';
    }

    if (!email.trim()) {
      errors.email = 'Email is required';
    } else if (!validateEmail(email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!password) {
      errors.password = 'Password is required';
    } else if (password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }

    if (isSignUp && password !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    if (isSignUp && subscriptionTiers.length > 0 && !selectedTier) {
      errors.tier = 'Please select a subscription plan';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setError(Object.values(errors)[0]); // Show first error as main message
      return;
    }

    setLoading(true);

    if (isSignUp && onSignUp) {
      try {
        await onSignUp(email, password, name, selectedTier || undefined);
      } catch (err: any) {
        setError(err.message || 'Sign up failed. Please try again.');
      } finally {
        setLoading(false);
      }
    } else {
      try {
        await onSignIn(email, password, isCreator);
      } catch (err: any) {
        const msg = err?.message || '';
        setError(
          /invalid credentials|unauthorized|401/i.test(msg)
            ? 'Incorrect email or password. Please try again.'
            : (msg || 'Sign in failed. Please try again.')
        );
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="signin-page">
      <div className="signin-container">
        <button className="back-btn" onClick={onBack}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back
        </button>

        <div className="signin-header">
          <div className="signin-logo">
            <div className="logo-icon">
              <img src="https://jalenward.com/images/blackwhitetextjalen.png" alt="OnlyJalen" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </div>
          </div>
          <h1>{isSignUp ? 'Create Account' : 'Welcome Back'}</h1>
          <p>{isSignUp ? 'Join as a subscriber to access exclusive content' : 'Sign in to continue to OnlyJalen'}</p>
        </div>

        {/* Three-bullet value strip — adds visible weight to an otherwise
            bare form and signals what the user gets after auth. Stays
            inside the signin-container so layout doesn't shift. */}
        <ul className="signin-value-strip" aria-label="What you get">
          <li><span className="svs-icon">📸</span><span>Exclusive photos &amp; videos, posted daily</span></li>
          <li><span className="svs-icon">💬</span><span>Direct messaging with the creator</span></li>
          <li><span className="svs-icon">🛍️</span><span>First access to drops &amp; merch</span></li>
        </ul>

        <form className="signin-form" onSubmit={handleSubmit} noValidate>
          {isSignUp && (
            <div className={`form-group ${fieldErrors.name ? 'has-error' : ''}`}>
              <label>Full Name</label>
              <input
                type="text"
                placeholder="Enter your name"
                value={name}
                onChange={e => { setName(e.target.value); setFieldErrors(prev => ({...prev, name: ''})); }}
                required
              />
              {fieldErrors.name && <span className="field-error">{fieldErrors.name}</span>}
            </div>
          )}

          <div className={`form-group ${fieldErrors.email ? 'has-error' : ''}`}>
            <label>Email</label>
            <input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={e => { setEmail(e.target.value); setFieldErrors(prev => ({...prev, email: ''})); }}
              required
            />
            {fieldErrors.email && <span className="field-error">{fieldErrors.email}</span>}
          </div>

          <div className={`form-group ${fieldErrors.password ? 'has-error' : ''}`}>
            <label>Password</label>
            <input
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={e => { setPassword(e.target.value); setFieldErrors(prev => ({...prev, password: ''})); }}
              required
              minLength={8}
            />
            {fieldErrors.password && <span className="field-error">{fieldErrors.password}</span>}
          </div>

          {isSignUp && (
            <div className={`form-group ${fieldErrors.confirmPassword ? 'has-error' : ''}`}>
              <label>Confirm Password</label>
              <input
                type="password"
                placeholder="Confirm your password"
                value={confirmPassword}
                onChange={e => { setConfirmPassword(e.target.value); setFieldErrors(prev => ({...prev, confirmPassword: ''})); }}
                required
              />
              {fieldErrors.confirmPassword && <span className="field-error">{fieldErrors.confirmPassword}</span>}
            </div>
          )}

          {isSignUp && subscriptionTiers.length > 0 && (
            <div className={`form-group ${fieldErrors.tier ? 'has-error' : ''}`}>
              <label>Select Subscription Plan</label>
              <div className="tier-selector">
                {subscriptionTiers.map(tier => (
                  <button
                    key={tier.id}
                    type="button"
                    className={`tier-option ${selectedTier === tier.id ? 'selected' : ''} ${tier.isPopular ? 'popular' : ''}`}
                    onClick={() => { setSelectedTier(tier.id); setFieldErrors(prev => ({...prev, tier: ''})); }}
                  >
                    {tier.isPopular && <span className="popular-badge">Most Popular</span>}
                    <span className="tier-name">{tier.name}</span>
                    <span className="tier-price">${tier.price}</span>
                    <ul className="tier-features">
                      {tier.features.slice(0, 2).map((feature, i) => (
                        <li key={i}>{feature}</li>
                      ))}
                    </ul>
                  </button>
                ))}
              </div>
              {fieldErrors.tier && <span className="field-error">{fieldErrors.tier}</span>}
            </div>
          )}

          {error && <div className="form-error" role="alert">{error}</div>}
          
          {!isSignUp && (
            <div className="form-options">
              <label className="remember-me">
                <input type="checkbox" />
                <span>Remember me</span>
              </label>
              <button
                type="button"
                className="forgot-link"
                onClick={() => {
                  window.location.href = 'mailto:support@onlyjalen.com?subject=Password%20reset&body=I%20need%20to%20reset%20my%20password.%20My%20email%20is:%20';
                }}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, font: 'inherit', color: 'inherit', textDecoration: 'underline' }}
              >
                Forgot password?
              </button>
            </div>
          )}
          
          <button type="submit" className="submit-btn" disabled={loading}>
            {loading ? (
              <span className="spinner"></span>
            ) : (
              isSignUp ? 'Create Account' : 'Sign In'
            )}
          </button>
        </form>

        <div className="signin-footer">
          {isSignUp ? (
            <p>Already have an account? <button onClick={() => setIsSignUp(false)}>Sign In</button></p>
          ) : (
            <p>Don't have an account? <button onClick={() => setIsSignUp(true)}>Sign Up</button></p>
          )}
        </div>
      </div>
    </div>
  );
};

export default SignInPage;
